import React, { useCallback, useState } from 'react';
import { getAPIErrorReason } from '../../helpers/common';
import SVGIcon from '../../assets/images/svg/SVGIcon';
import {
  ActionButton,
  CloseButton,
  Divider,
  ImageContainer,
  PreviewActions,
  PreviewHeader,
  PreviewImage,
  PreviewWrapper,
  StyledImage,
  ScreenshotToolbar,
  ToolbarButton,
  ToolbarGroup,
  ImageDiv,
  ImageMask,
  ImageMaskContent,
  MaskButton
} from './styles';
import { IMAGE_TYPE, ImageType } from '../../global/constants';
import { dispatchTaskError, dispatchTaskSuccess } from '../../services/taskServices';
import { useHotkeys } from 'react-hotkeys-hook';
import { createPortal } from 'react-dom';
import copy from 'clipboard-copy';
import { dispatchProjectSuccess } from '../../services/projectServices';

interface CustomImagePreviewProps {
  src: string;
  alt?: string;
  width?: number | string;
  height?: number | string;
  className?: string;
  style?: React.CSSProperties;
  isInbox?: boolean;
  type?: ImageType;
  ShowImageOptions?: boolean;
  ShowImageBorder?: boolean;
}

export const CustomImagePreview: React.FC<CustomImagePreviewProps> = ({
  src,
  alt = '',
  width,
  height,
  className,
  style,
  isInbox,
  type,
  ShowImageOptions,
  ShowImageBorder
}) => {
  const [isPreviewVisible, setIsPreviewVisible] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [flipH, setFlipH] = useState(false);
  const [flipV, setFlipV] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [startPos, setStartPos] = useState({ x: 0, y: 0 });

  const handleMouseDown = (e: React.MouseEvent) => {
    if (e.target !== e.currentTarget) return; // Only allow dragging when clicking on the image itself
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
    setStartPos({ x: e.clientX - position.x, y: e.clientY - position.y });
    document.body.style.cursor = 'grabbing';
  };

  // Fix the linter error by using an arrow function
  const throttle = (func: (...args: any[]) => void, limit: number) => {
    let inThrottle: boolean;
    return (...args: any[]) => {
      if (!inThrottle) {
        func(...args);
        inThrottle = true;
        setTimeout(() => (inThrottle = false), limit);
      }
    };
  };

  const throttledMouseMove = throttle((e: React.MouseEvent) => {
    if (!isDragging) return;
    e.preventDefault();
    e.stopPropagation();
    const newX = e.clientX - startPos.x;
    const newY = e.clientY - startPos.y;
    requestAnimationFrame(() => {
      setPosition({ x: newX, y: newY });
    });
  }, 16); // Approximately 60fps

  const handleMouseUp = (e: React.MouseEvent) => {
    if (!isDragging) return;
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    document.body.style.cursor = 'default';
    if (zoom === 1) {
      setPosition({ x: 0, y: 0 }); // Reset position if at actual size
    }
  };

  // Get file name from URL
  const getFilename = (url: string) => {
    const decodedUrl = decodeURIComponent(url);
    const parts = decodedUrl.split('/');
    const fullFilename = parts?.pop()?.split('?')[0];
    const nameWithoutSuffix = fullFilename?.replace(/_(\d+)(\.\w+)$/, '$2');
    return nameWithoutSuffix;
  };

  // Handle image download
  const handleDownload = useCallback(
    async (e: React.MouseEvent) => {
      try {
        e.stopPropagation();
        const response = await fetch(src);
        const blob = await response.blob();
        const blobUrl = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = getFilename(src) || 'downloaded_image.png';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(blobUrl);
      } catch (error) {
        dispatchTaskError(getAPIErrorReason(error) || 'Something went wrong!');
      }
    },
    [src]
  );

  const handleCopyUrl = useCallback(
    async (e: React.MouseEvent) => {
      e.stopPropagation();
      await copy(src);
      dispatchProjectSuccess('Image url copied successfully!');
    },
    [src]
  );

  // Handle copy to clipboard
  const handleCopy = useCallback(
    async (e: React.MouseEvent) => {
      try {
        e.stopPropagation();
        const response = await fetch(src);
        const blob = await response.blob();

        const imageBitmap = await createImageBitmap(blob);
        const canvas = document.createElement('canvas');
        canvas.width = imageBitmap.width;
        canvas.height = imageBitmap.height;
        const ctx = canvas.getContext('2d');

        if (ctx) {
          ctx.drawImage(imageBitmap, 0, 0);
          const pngBlob = await new Promise<Blob>((resolve) =>
            canvas.toBlob((blob) => blob && resolve(blob), 'image/png')
          );

          const clipboardItem = new ClipboardItem({ 'image/png': pngBlob });
          await navigator.clipboard.write([clipboardItem]);
          dispatchTaskSuccess('Image copied to clipboard!');
        }
      } catch (error) {
        dispatchTaskError(getAPIErrorReason(error));
      }
    },
    [src]
  );

  const resetPreviewState = () => {
    setZoom(1);
    setRotation(0);
    setFlipH(false);
    setFlipV(false);
    setPosition({ x: 0, y: 0 });
  };

  const togglePreview = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isPreviewVisible) {
      resetPreviewState();
    }
    setIsPreviewVisible(!isPreviewVisible);
  };

  const handleWheelZoom = useCallback(
    (e: React.WheelEvent) => {
      e.stopPropagation();
      if (e.deltaY < 0 && zoom < 3) {
        setZoom((prev) => Math.min(prev + 0.1, 3)); // Zoom in
      } else if (e.deltaY > 0 && zoom > 1) {
        setZoom((prev) => Math.max(prev - 0.1, 1)); // Zoom out
      }
    },
    [zoom]
  );

  const handleRotate = useCallback((angle: number) => {
    setRotation((prev) => (prev + angle) % 360);
  }, []);

  const handleFlipHorizontal = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setFlipV((prev) => !prev);
  }, []);

  const handleFlipVertical = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setFlipH((prev) => !prev);
  }, []);

  const handleZoomIn = useCallback(() => {
    if (zoom < 3) {
      setZoom((prev) => Math.min(prev + 0.1, 3));
    }
  }, [zoom]);

  const handleZoomOut = useCallback(() => {
    if (zoom > 1) {
      setZoom((prev) => Math.max(prev - 0.1, 1));
    }
  }, [zoom]);

  const isZoomOutDisabled = zoom <= 1;
  const isZoomInDisabled = zoom >= 3;

  useHotkeys('esc', async (event) => {
    event.preventDefault();
    setIsPreviewVisible(false);
  });

  const previewContent = (
    <PreviewWrapper
      onMouseDown={handleMouseDown}
      onMouseMove={throttledMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
      onWheel={handleWheelZoom}
      onClick={(e) => e.stopPropagation()} // Prevent closing when clicking inside
      style={{ cursor: 'default' }} // Ensure default cursor outside image
    >
      <PreviewHeader>
        <PreviewActions>
          <ActionButton onClick={handleDownload}>
            <SVGIcon name='download-icon' width='20' height='20' viewBox='0 0 18 18' fill='var(--text-white)' />
          </ActionButton>
          <ActionButton onClick={handleCopy}>
            <SVGIcon name='copy-image-icon' width='20' height='20' viewBox='0 0 18 18' fill='var(--text-white)' />
          </ActionButton>
        </PreviewActions>
        <Divider />
        <CloseButton onClick={() => setIsPreviewVisible(false)}>
          <SVGIcon name='close-preview-icon' width='20' height='20' viewBox='0 0 18 18' fill='var(--text-white)' />
        </CloseButton>
      </PreviewHeader>
      <ImageDiv
        onClick={(e) => {
          togglePreview(e);
        }}
        // handle releasing outside the image
      >
        <PreviewImage
          src={src}
          alt={alt}
          zoom={zoom}
          rotation={rotation}
          flipH={flipH}
          flipV={flipV}
          style={{
            transform: `
        scale(${zoom})
        rotate(${rotation}deg)
        scaleX(${flipH ? -1 : 1})
        scaleY(${flipV ? -1 : 1})
        translate(${position.x}px, ${position.y}px)
      `,
            transition: isDragging ? 'none' : 'transform 0.3s ease-out'
          }}
          onMouseDown={handleMouseDown}
          onMouseMove={throttledMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
          onClick={(e) => {
            e.stopPropagation();
          }}
        />
      </ImageDiv>

      <ScreenshotToolbar onClick={(e) => e.stopPropagation()}>
        <ToolbarGroup>
          <ToolbarButton onClick={handleFlipHorizontal}>
            <SVGIcon name='flip-h-icon' width='20' height='20' viewBox='0 0 20 20' />
          </ToolbarButton>
          <ToolbarButton onClick={handleFlipVertical}>
            <SVGIcon name='flip-v-icon' width='20' height='20' viewBox='0 0 20 20' />
          </ToolbarButton>
          <ToolbarButton onClick={() => handleRotate(-90)}>
            <SVGIcon name='rotate-left-icon' width='20' height='20' viewBox='0 0 20 20' />
          </ToolbarButton>
          <ToolbarButton onClick={() => handleRotate(90)}>
            <SVGIcon name='rotate-right-icon' width='20' height='20' viewBox='0 0 20 20' />
          </ToolbarButton>
          <ToolbarButton
            onClick={handleZoomOut}
            disabled={isZoomOutDisabled}
            style={{
              opacity: isZoomOutDisabled ? 0.5 : 1,
              cursor: isZoomOutDisabled ? 'not-allowed' : 'pointer',
              pointerEvents: isZoomOutDisabled ? 'none' : 'auto'
            }}>
            <SVGIcon
              name='zoom-out-icon'
              width='20'
              height='20'
              viewBox='0 0 20 20'
              style={{
                fill: isZoomOutDisabled ? 'var(--text-placeholder)' : 'var(--text-white)'
              }}
            />
          </ToolbarButton>
          <ToolbarButton
            onClick={handleZoomIn}
            disabled={isZoomInDisabled}
            style={{
              opacity: isZoomInDisabled ? 0.5 : 1,
              cursor: isZoomInDisabled ? 'not-allowed' : 'pointer',
              pointerEvents: isZoomInDisabled ? 'none' : 'auto'
            }}>
            <SVGIcon
              name='zoom-in-icon'
              width='20'
              height='20'
              viewBox='0 0 20 20'
              style={{
                fill: isZoomInDisabled ? 'var(--text-placeholder)' : 'var(--text-white)'
              }}
            />
          </ToolbarButton>
        </ToolbarGroup>
      </ScreenshotToolbar>
    </PreviewWrapper>
  );

  return (
    <ImageContainer
      className={`${type === IMAGE_TYPE.FIGMA ? 'figma-image' : ''} ${className ? ` ${className}` : ''} ${
        type === IMAGE_TYPE.PRINT_SCREEN ? 'print-screen-image' : type === IMAGE_TYPE.IMAGE ? 'image-preview' : ''
      }`}
      style={style}
      onClick={() => {
        setIsPreviewVisible(false);
        resetPreviewState(); // Reset state when closing by clicking outside
      }}>
      <StyledImage
        src={src}
        alt={alt}
        width={width}
        height={height}
        onClick={togglePreview}
        style={{ cursor: isDragging ? 'grabbing' : 'grab' }} // Set cursor style here
      />
      {ShowImageOptions ? (
        <ImageMask onClick={togglePreview}>
          <ImageMaskContent
            onClick={(e) => e.stopPropagation()} // Prevents triggering preview on icon click
          >
            <MaskButton onClick={handleCopy}>
              <SVGIcon name='copy-image-icon' width='16' height='16' viewBox='0 0 18 18' />
            </MaskButton>
            <MaskButton onClick={handleDownload}>
              <SVGIcon name='download-icon' width='16' height='16' viewBox='0 0 18 18' />
            </MaskButton>
            <MaskButton onClick={handleCopyUrl}>
              <SVGIcon name='copy-link-icon' width='16' height='16' viewBox='0 0 18 18' />
            </MaskButton>
          </ImageMaskContent>
        </ImageMask>
      ) : (
        <ImageMask showImageBorder={ShowImageBorder} onClick={togglePreview} />
      )}
      {isPreviewVisible && (isInbox ? createPortal(previewContent, document.body) : previewContent)}
    </ImageContainer>
  );
};
